/* ──────────────────────────────────────────────────────────────────────────
 * Predictive risk & bottleneck engine.
 *
 * The rest of the platform reports state: this zone is 92% full, that
 * incident is open. A command room needs the next question answered — which
 * zone breaks next, how long have we got, and what is causing it. Lead time
 * is the entire value of a control room; a red dot that turns red at the
 * moment of failure has told nobody anything they could act on.
 *
 * Closes the §2 requirements that state-reporting alone does not cover:
 * congestion hotspots, crowd accumulation, abnormal movement, bottlenecks.
 *
 * ── EVERY OUTPUT HERE IS A PROJECTION FROM SIMULATED INPUTS ──
 * The occupancy and flow figures underneath are fabricated demo data, so a
 * correct model over them still yields a fabricated conclusion. Each result
 * carries TRUST.SIMULATED and names the assumptions it rests on. The models
 * are deliberately simple and legible — an operator must be able to argue
 * with the arithmetic, which is impossible with a black box.
 * ────────────────────────────────────────────────────────────────────────── */

import { TRUST } from "../data/provenance";
import { SECTORS, ENTRY_GATES, EXIT_GATES, STATUS_THRESHOLDS } from "../data/kumbhGeo";
import { FLOW_VECTORS, INCIDENTS, SEVERITY, activeIncidents } from "../data/operational";

/* ── Stated planning assumptions ───────────────────────────────────────
 * These are the numbers a department would replace with surveyed figures.
 * They are constants here so they can be found, argued with and changed in
 * one place, rather than buried in a formula. */
export const ASSUMPTIONS = {
  /* Persons per minute a single managed gate can pass. A common crowd-safety
   * planning figure for a controlled pedestrian gate. Real throughput depends
   * on width, screening, barrier layout and staffing. */
  GATE_THROUGHPUT_PER_MIN: 40,
  /* Flow rates in the demo data are persons/hour. */
  FLOW_RATE_IS_PER_HOUR: true,
  /* A zone is "accumulating" when net inflow exceeds this share of capacity
   * per hour — enough to matter within an operational shift. */
  ACCUMULATION_PCT_PER_HOUR: 3,
  /* Lead time below which a projection is treated as actionable now. */
  URGENT_LEAD_MINUTES: 45,
};

const simTrust = TRUST.SIMULATED;

/* Net inbound rate for a sector, persons per minute.
 * Bearing is used to classify a vector as inbound or outbound relative to
 * the sector it is attached to: the demo data attaches each vector to its
 * origin sector, so a vector pointing away is outbound. This is a coarse
 * proxy — real systems count across a line, not by bearing. */
function netFlowPerMin(sectorId, flows = FLOW_VECTORS) {
  const mine = flows.filter((f) => f.from === sectorId);
  if (!mine.length) return { inPerMin: 0, outPerMin: 0, netPerMin: 0, vectors: 0 };

  // Without a true inbound/outbound classification in the demo data, treat
  // the attached rate as gross throughput and split it by bearing quadrant:
  // vectors pointing broadly north/east read as inbound to the theatre.
  let inRate = 0;
  let outRate = 0;
  mine.forEach((f) => {
    const inbound = f.bearing >= 315 || f.bearing < 135;
    if (inbound) inRate += f.rate; else outRate += f.rate;
  });

  const div = ASSUMPTIONS.FLOW_RATE_IS_PER_HOUR ? 60 : 1;
  return {
    inPerMin: inRate / div,
    outPerMin: outRate / div,
    netPerMin: (inRate - outRate) / div,
    vectors: mine.length,
  };
}

/* ── Time to critical ──────────────────────────────────────────────────
 * The headline number: at the current net rate, how long until this sector
 * crosses the red threshold. Returns null when it is not on a path to
 * critical, rather than a large number that would imply a prediction. */
export function timeToCritical(sector, flows = FLOW_VECTORS) {
  const { netPerMin } = netFlowPerMin(sector.id, flows);
  const redAt = sector.capacity * STATUS_THRESHOLDS.red;
  const headroom = redAt - sector.current;

  if (sector.current >= redAt) return { minutes: 0, state: "already-critical", netPerMin };
  if (netPerMin <= 0) return { minutes: null, state: "stable-or-draining", netPerMin };

  return { minutes: Math.round(headroom / netPerMin), state: "filling", netPerMin };
}

/* ── Bottleneck / chokepoint detection (§2) ───────────────────────────
 * Compares demand at a sector's gates against what those gates can pass.
 * A ratio above 1 means more people are arriving than the gates can clear,
 * which is the mechanism behind most crowd-crush incidents — the crowd does
 * not disperse, it stacks. */
export function detectBottlenecks(flows = FLOW_VECTORS) {
  return SECTORS.map((s) => {
    const { inPerMin } = netFlowPerMin(s.id, flows);
    const entryCount = ENTRY_GATES.filter((g) => g.sector === s.id).length;
    const exitCount = EXIT_GATES.filter((g) => g.sector === s.id).length;

    const entryCapacity = entryCount * ASSUMPTIONS.GATE_THROUGHPUT_PER_MIN;
    const ratio = entryCapacity > 0 ? inPerMin / entryCapacity : null;

    return {
      sectorId: s.id,
      name: s.name,
      centre: s.centre,
      inPerMin: Math.round(inPerMin),
      entryGates: entryCount,
      exitGates: exitCount,
      entryCapacityPerMin: entryCapacity,
      /* null when a sector has no mapped gates — an unknown, not a zero.
       * Reporting 0 would read as "no pressure" when it means "no data". */
      demandRatio: ratio === null ? null : Number(ratio.toFixed(2)),
      isChokepoint: ratio !== null && ratio > 1,
      noGatesMapped: entryCount === 0,
    };
  }).sort((a, b) => (b.demandRatio ?? -1) - (a.demandRatio ?? -1));
}

/* ── Accumulation (§2) ─────────────────────────────────────────────────
 * Net inflow with nowhere to go. Distinct from a bottleneck: a sector can
 * clear its gates fine and still fill up because nobody is leaving. */
export function detectAccumulation(flows = FLOW_VECTORS) {
  return SECTORS.map((s) => {
    const { inPerMin, outPerMin, netPerMin } = netFlowPerMin(s.id, flows);
    const pctPerHour = (netPerMin * 60 / s.capacity) * 100;
    return {
      sectorId: s.id,
      name: s.name,
      centre: s.centre,
      inPerMin: Math.round(inPerMin),
      outPerMin: Math.round(outPerMin),
      netPerMin: Math.round(netPerMin),
      pctPerHour: Number(pctPerHour.toFixed(1)),
      isAccumulating: pctPerHour >= ASSUMPTIONS.ACCUMULATION_PCT_PER_HOUR,
    };
  }).filter((r) => r.isAccumulating)
    .sort((a, b) => b.pctPerHour - a.pctPerHour);
}

/* ── Abnormal movement (§2) ────────────────────────────────────────────
 * Flags flow that contradicts the surrounding pattern. With no historical
 * baseline we cannot detect a deviation from normal, so we detect internal
 * inconsistency instead: a sector whose flow opposes its neighbours', which
 * in a pilgrimage corridor usually means counter-flow — people pushing back
 * against the stream. That is a genuine precursor to crush conditions.
 *
 * Honest limit: this is NOT anomaly detection against a learned baseline.
 * That needs history, which the POC has no store for. */
export function detectAbnormalMovement(flows = FLOW_VECTORS) {
  if (flows.length < 2) return [];
  // Circular mean of all bearings — the theatre's dominant direction.
  const rad = (d) => (d * Math.PI) / 180;
  const sx = flows.reduce((a, f) => a + Math.cos(rad(f.bearing)), 0) / flows.length;
  const sy = flows.reduce((a, f) => a + Math.sin(rad(f.bearing)), 0) / flows.length;
  const dominant = (Math.atan2(sy, sx) * 180) / Math.PI;

  return flows.map((f) => {
    let diff = Math.abs(f.bearing - (dominant + 360) % 360);
    if (diff > 180) diff = 360 - diff;
    return {
      id: f.id,
      sectorId: f.from,
      coord: f.coord,
      bearing: f.bearing,
      deviationDeg: Math.round(diff),
      isCounterFlow: diff > 120,
      rate: f.rate,
    };
  }).filter((r) => r.isCounterFlow);
}

/* ── Composite zone risk ───────────────────────────────────────────────
 * One 0-100 number per sector, with its drivers listed. The drivers matter
 * more than the score: a number an operator cannot decompose is a number
 * they cannot act on or defend afterwards.
 *
 * Weights are explicit and stated in the UI. They are a judgement, not a
 * calibration — there is no outcome data here to fit them against. */
export const RISK_WEIGHTS = {
  occupancy: 40,   // how full it is now
  leadTime: 30,    // how soon it breaks
  incidents: 20,   // active incident load, severity-weighted
  bottleneck: 10,  // gate demand vs capacity
};

export function computeZoneRisk({ flows = FLOW_VECTORS, incidents = INCIDENTS } = {}) {
  const bottlenecks = detectBottlenecks(flows);
  const bottleneckBy = Object.fromEntries(bottlenecks.map((b) => [b.sectorId, b]));
  const active = incidents.filter((i) => i.status !== "Resolved");

  const rows = SECTORS.map((s) => {
    const occupancy = s.current / s.capacity;
    const ttc = timeToCritical(s, flows);
    const bn = bottleneckBy[s.id];

    const sectorIncidents = active.filter((i) => i.sector === s.id);
    const incidentLoad = sectorIncidents.reduce(
      (sum, i) => sum + (SEVERITY[i.severity]?.rank ?? 0) + 1, 0
    );

    // Each component normalised to 0..1 before weighting.
    const cOcc = Math.min(1, occupancy / STATUS_THRESHOLDS.red);
    const cLead =
      ttc.minutes === 0 ? 1
      : ttc.minutes == null ? 0
      : Math.max(0, 1 - ttc.minutes / 180);          // 3h horizon
    const cInc = Math.min(1, incidentLoad / 8);
    const cBn = bn?.demandRatio == null ? 0 : Math.min(1, bn.demandRatio / 1.5);

    const score = Math.round(
      cOcc * RISK_WEIGHTS.occupancy +
      cLead * RISK_WEIGHTS.leadTime +
      cInc * RISK_WEIGHTS.incidents +
      cBn * RISK_WEIGHTS.bottleneck
    );

    const drivers = [];
    if (cOcc >= 0.85) drivers.push({ k: "occupancy", text: `${Math.round(occupancy * 100)}% occupied` });
    if (ttc.minutes === 0) drivers.push({ k: "leadTime", text: "already at critical threshold" });
    else if (ttc.minutes != null && ttc.minutes <= ASSUMPTIONS.URGENT_LEAD_MINUTES)
      drivers.push({ k: "leadTime", text: `~${ttc.minutes} min to critical at current rate` });
    if (sectorIncidents.length)
      drivers.push({ k: "incidents", text: `${sectorIncidents.length} active incident(s)` });
    if (bn?.isChokepoint)
      drivers.push({ k: "bottleneck", text: `gate demand ${bn.demandRatio}× capacity` });

    return {
      sectorId: s.id,
      name: s.name,
      centre: s.centre,
      score,
      band: score >= 70 ? "red" : score >= 45 ? "amber" : "green",
      occupancyPct: Math.round(occupancy * 100),
      minutesToCritical: ttc.minutes,
      ttcState: ttc.state,
      netPerMin: Math.round(ttc.netPerMin),
      incidentCount: sectorIncidents.length,
      bottleneck: bn,
      drivers,
      trust: simTrust,
    };
  });

  return rows.sort((a, b) => b.score - a.score);
}

/* One call for the panel: everything the risk view needs, plus the
 * assumptions that produced it. */
export function riskHorizon(opts = {}) {
  const zones = computeZoneRisk(opts);
  const bottlenecks = detectBottlenecks(opts.flows).filter((b) => b.isChokepoint);
  const accumulation = detectAccumulation(opts.flows);
  const abnormal = detectAbnormalMovement(opts.flows);

  const soonest = zones
    .filter((z) => z.minutesToCritical != null && z.minutesToCritical > 0)
    .sort((a, b) => a.minutesToCritical - b.minutesToCritical)[0] || null;

  return {
    zones,
    bottlenecks,
    accumulation,
    abnormal,
    soonest,
    trust: simTrust,
    assumptions: [
      `Gate throughput assumed ${ASSUMPTIONS.GATE_THROUGHPUT_PER_MIN} persons/min per managed gate — a planning figure, not a surveyed one.`,
      "Flow direction is classified by bearing quadrant, not by counting across a line.",
      "Time-to-critical extrapolates the current net rate linearly; real arrival rates are not linear and peak sharply around Shahi Snan timings.",
      "Abnormal movement is counter-flow against the dominant direction, NOT deviation from a learned baseline — no historical store exists to learn one.",
      "Risk weights are a stated judgement, not calibrated against outcomes.",
    ],
  };
}
